import { Badge, Chip } from '@orbitalwatch/design';
import { KIND_LABEL, KIND_TONE, stateTone } from './tones';
import type { ChangeKind } from './tones';

export type { ChangeKind, ChangeRequestState } from './tones';

export interface StateChipProps {
    /** The state as the API names it ("Proposed"); unknown values render neutral. */
    state: string;
    /** Visible text when it differs from the state ("Changes requested"). */
    label?: string;
    size?: 'sm' | 'md';
    className?: string;
}

/** A change-request state: library `Chip` with the state dot, toned per the inventory (Proposed = warning, …). */
export function StateChip(props: StateChipProps) {
    return (
        <Chip tone={stateTone(props.state)} dot size={props.size ?? 'md'} className={props.className}>
            {props.label ?? props.state}
        </Chip>
    );
}

export interface KindDotProps {
    kind: ChangeKind;
    /** `dot`: an 8 px coloured dot with the label as its accessible name (tree rows); `chip`: a library `Chip` (pane head). Default `dot`. */
    variant?: 'dot' | 'chip';
    /** Visible / accessible text; default the kind's label ("Edited"). */
    label?: string;
    className?: string;
}

/**
 * The kind of a changed page. `chip` is a library `Chip`; `dot` is a DS gap (the library has no standalone status dot):
 * 8 px, tone colour, `role="img"` named by the label (`ds.css` "kind dot").
 */
export function KindDot(props: KindDotProps) {
    const label = props.label ?? KIND_LABEL[props.kind];
    if (props.variant === 'chip') {
        return <Chip tone={KIND_TONE[props.kind]} size="sm" className={props.className}>{label}</Chip>;
    }
    return <span className={['ark-kind-dot', `ark-tone-${KIND_TONE[props.kind]}`, props.className].filter(Boolean).join(' ')} role="img" aria-label={label} title={label} />;
}

export interface CountBadgeProps {
    count: number;
    /** What the number counts ("3 proposed change requests"). */
    'aria-label': string;
    /** `accent` (needs attention) or `neutral`. Default `accent`. */
    tone?: 'accent' | 'neutral';
    showZero?: boolean;
    className?: string;
}

/** A count pill: library `Badge` (hidden at 0 unless `showZero`). */
export function CountBadge(props: CountBadgeProps) {
    return <Badge count={props.count} tone={props.tone ?? 'accent'} showZero={props.showZero} aria-label={props['aria-label']} className={props.className} />;
}
